import { Inject, Injectable } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

import { AppLang } from '../lang/app-lang';
import { registerAppLocaleData } from '../lang/locale-data';

/** Догрузка словаря языка по требованию (ленивый JSON-чанк). Возвращает translation-объект. */
export type I18nBundleLoader = (lang: AppLang) => Promise<unknown>;

const TRANSLATION_NAMESPACE = 'translation';

/**
 * Регистр подгруженных словарей. provideI18n помечает eager-ресурсы
 * (markLoaded) и сохраняет загрузчик остальных (registerLoader); смена языка
 * сначала зовёт ensure(), поэтому i18next.changeLanguage всегда видит бандл.
 */
@Injectable({ providedIn: 'root' })
export class I18nBundleService {
  private loader: I18nBundleLoader | null = null;
  private readonly loaded: Set<string> = new Set<string>();

  constructor(@Inject(I18NEXT_SERVICE) private readonly i18next: ITranslationService) {}

  registerLoader(loader: I18nBundleLoader | null | undefined): void {
    this.loader = loader ?? null;
  }

  markLoaded(lang: AppLang): void {
    this.loaded.add(lang);
  }

  isLoaded(lang: AppLang): boolean {
    return this.loaded.has(lang);
  }

  async ensure(lang: AppLang): Promise<void> {
    const loader = this.loader;
    if (loader) {
      await registerAppLocaleData(lang);
    }
    if (this.loaded.has(lang)) {
      return;
    }
    if (!loader) {
      return;
    }
    const bundle = await loader(lang);
    this.i18next.addResourceBundle(lang, TRANSLATION_NAMESPACE, bundle);
    this.loaded.add(lang);
  }
}
